<script setup lang="ts">
  // Vue core imports
  import type { PropType } from 'vue'
  import { computed, onMounted, ref, watch } from 'vue'

  // External library imports

  import { string } from 'yup'

  import { useGlobal } from '@common/core-layer/composables/useGlobal'

  import { useI18n } from 'vue-i18n'

  import CPButton from '@ui/components/CPButton.vue'

  import CPDivider from '@ui/components/CPDivider.vue'

  import CPForm from '@ui/components/CPForm.vue'

  import CPFormItem from '@ui/components/CPFormItem.vue'

  import CPInput from '@ui/components/CPInput.vue'

  import CPText from '@ui/components/CPText.vue'

  // Internal imports
  import SignInWithGoogle from './SignInWithGoogle.vue'

  import SignInWithGoogleKollab from './SignInWithGoogleKollab.vue'

  import { createElementId } from '../../../core-layer/utils'

  import { useAuth } from '../../composables/useAuth'

  import { LoginEvents, Screens } from '../../types'

  import { getErrorMessage } from '../../utils'

  import {
    isDeferredInPlacePasswordLoginResult,
    shouldShowSetPasswordAfterLoginResponse,
    buildSetPasswordScreenChange,
  } from '../../utils/inPlacePasswordDefer'

  import ReCaptcha from '../captcha/ReCaptcha.vue'

  // Props definitions
  const props = defineProps({
    referrerUser: {
      type: Object as PropType<{
        email: string
        name: string
        _id?: string
      }>,
      default: null,
      required: false,
    },
    email: {
      type: String,
      default: '',
    },
    isSSODisabled: {
      type: Boolean,
      default: false,
    },
    /** Clientportal opt-in — forwarded to Google SSO (non-Kollab). */
    deferSetPasswordSession: {
      type: Boolean,
      default: false,
    },
  })

  // Emits
  const emit = defineEmits<{
    (e: 'changeScreen', data: any): void
    (e: 'loginCallback', data: any): void
  }>()

  // Composables
  const { loginWithEmailPassword } = useAuth()

  const { isGokollab } = useGlobal()

  const { t: $t } = useI18n()

  // Refs and reactive variables
  const errorMessage = ref()

  const loading = ref(false)

  const showRecaptcha = ref(false)

  const reCaptchaToken = ref(undefined)

  const loginDisabled = ref(false)

  const passwordError = ref('')

  const emailError = ref('')

  const formRef = ref()

  const formValue = ref({
    email: props.email || '',
    password: '',
  })

  const rules = {
    email: {
      required: true,
      asyncValidator: async (_: any, value: string) => {
        emailError.value = ''

        if (!value) {
          emailError.value = $t('auth.validation.emailRequired')
          return Promise.reject(new Error($t('auth.validation.emailRequired')))
        }

        try {
          await string().email().required().validate(value)
          return Promise.resolve()
        } catch (error: any) {
          emailError.value = error.message || $t('auth.validation.emailInvalid')
          return Promise.reject(new Error(emailError.value))
        }
      },
      trigger: ['input', 'blur'],
    },
    password: {
      required: true,
      min: 8,
      validator: (_: unknown, value: string) => {
        passwordError.value = ''

        if (!value) {
          passwordError.value = $t('auth.validation.passwordRequired')
          return false
        }

        if (value.length < 8) {
          passwordError.value = $t('auth.validation.passwordMinLength')
          return false
        }

        return true
      },
      trigger: ['input', 'blur'],
    },
  }

  const onVerify = (response: any) => {
    reCaptchaToken.value = response
    loginDisabled.value = false
  }

  const onError = (response: any) => {
    loginDisabled.value = true
  }

  const onExpired = (response: any) => {
    reCaptchaToken.value = undefined
    loginDisabled.value = true
  }

  const isFormValid = computed(() => {
    return (
      formValue.value.email && formValue.value.password && formValue.value.password.length >= 8 && !emailError.value && !passwordError.value
    )
  })

  // Lifecycle hooks
  onMounted(() => {
    if (props.email) {
      formValue.value.email = props.email
    }
  })

  // Watch effects
  watch(
    () => props.email,
    (newEmail: string) => {
      if (newEmail) {
        formValue.value.email = newEmail
      }
    }
  )

  watch(formValue.value, () => {
    errorMessage.value = null
  })

  watch(
    () => formValue.value.password,
    () => {
      passwordError.value = ''
    }
  )

  watch(
    () => formValue.value.email,
    () => {
      emailError.value = ''
    }
  )

  async function validateForm() {
    passwordError.value = ''
    try {
      await formRef.value.getForm().validate()
      return true
    } catch (e: unknown) {
      console.error('error', e)

      // Check if there are password-specific validation errors
      const form = formRef.value.getForm()
      const passwordFieldErrors = form.getFieldError('password')

      if (passwordFieldErrors && passwordFieldErrors.length > 0) {
        passwordError.value = passwordFieldErrors[0]
      }

      return false
    }
  }

  async function handleValidateClick(e: Event) {
    e.preventDefault()
    if (loginDisabled.value) {
      return
    }
    errorMessage.value = null
    const result = await validateForm()
    if (!result) {
      return
    }
    loading.value = true
    try {
      const email = formValue.value.email.trim()
      const result = await loginWithEmailPassword(email, formValue.value.password.trim(), reCaptchaToken.value, {
        deferSessionUntilInPlacePasswordSet: props.deferSetPasswordSession,
      })

      // Deferred in-place Set Password — session not yet persisted
      if (isDeferredInPlacePasswordLoginResult(props.deferSetPasswordSession, result)) {
        emit('changeScreen', buildSetPasswordScreenChange(email, result.data))
        showRecaptcha.value = false
        loading.value = false
        return
      }

      const data = result.data
      if (data) {
        if (data.multipleUsers) {
          emit('changeScreen', {
            page: Screens.SELECT_USER_MULTI_USER,
            data: {
              ...formValue.value,
              multiUser: data.multipleUsers,
            },
          })
        } else if (data.token) {
          // Non-deferred: session already saved, but API still signals password unset
          if (shouldShowSetPasswordAfterLoginResponse(data)) {
            emit('changeScreen', buildSetPasswordScreenChange(email, data))
          } else {
            emit('loginCallback', {
              eventType: LoginEvents.LOGIN_SUCCESSFUL,
              data: { ...data },
            })
          }
        }
      }
      showRecaptcha.value = false
    } catch (error: any) {
      console.error('Login error:', error)
      if (error?.response?.status === 429) {
        loginDisabled.value = true
        showRecaptcha.value = true
      } else {
        showRecaptcha.value = false
        errorMessage.value = getErrorMessage(error)
      }
    } finally {
      loading.value = false
    }
  }
</script>

<template>
  <div>
    <SignInWithGoogleKollab
      v-if="!props.isSSODisabled && isGokollab"
      login-type="SIGN_IN"
      :referrer-user="referrerUser"
      @changeScreen="(val: any) => emit('changeScreen', val)"
      @loginCallback="(val: any) => emit('loginCallback', val)"
    />
    <SignInWithGoogle
      v-else-if="!props.isSSODisabled"
      login-type="SIGN_IN"
      :referrer-user="referrerUser"
      :defer-set-password-session="deferSetPasswordSession"
      @changeScreen="(val: any) => emit('changeScreen', val)"
      @loginCallback="(val: any) => emit('loginCallback', val)"
    />

    <CPForm ref="formRef" class="" :rules="rules" :model="formValue" @keyup.enter="handleValidateClick">
      <CPFormItem
        :id="createElementId('login-modal', 'email', 'form-item')"
        class="w-full"
        :label="$t('auth.email')"
        required
        path="email"
        :show-require-mark="true"
        label-size="lg"
      >
        <CPInput
          :id="createElementId('login-modal', 'email', 'input')"
          v-model="formValue.email"
          size="sm"
          :placeholder="$t('auth.emailPlaceholder')"
        />
        <template #feedback>
          <CPText :id="createElementId('login-modal', 'email-field', 'feedback')" size="lg" :class="{ 'text-danger': emailError }">
            {{ emailError }}
          </CPText>
        </template>
      </CPFormItem>

      <CPFormItem
        :id="createElementId('login-modal', 'password', 'form-item')"
        class="w-full"
        :label="$t('auth.password')"
        required
        path="password"
        :show-require-mark="true"
        label-size="lg"
      >
        <CPInput
          :id="createElementId('login-modal', 'password', 'input')"
          v-model="formValue.password"
          size="sm"
          type="password"
          :placeholder="$t('auth.passwordPlaceholder')"
        />
        <template #feedback>
          <div class="flex w-full items-center justify-between">
            <CPText :id="createElementId('login-modal', 'password-field', 'feedback')" size="lg" :class="{ 'text-danger': passwordError }">
              {{ passwordError }}
            </CPText>
            <CPButton
              :id="createElementId('login-modal', 'reset-password', 'button')"
              type="text"
              color="primary"
              size="xs"
              class="ml-2 flex-shrink-0 cursor-pointer hover:underline"
              @click="
                emit('changeScreen', {
                  page: Screens.FORGOT_PASSWORD,
                  data: { email: formValue.email },
                })
              "
            >
              {{ $t('auth.resetPassword') }}
            </CPButton>
          </div>
        </template>
      </CPFormItem>

      <div v-if="errorMessage" class="text-danger mb-3 w-full">
        <CPText :id="createElementId('login-modal', 'error', 'message')" size="sm">{{ errorMessage }}</CPText>
      </div>

      <ReCaptcha v-if="showRecaptcha" class="flex w-full justify-center" @expired="onExpired" @verify="onVerify" @error="onError" />

      <CPButton
        :id="createElementId('login-modal', 'email-password', 'button')"
        class="w-full rounded-lg"
        type="primary"
        size="md"
        :disabled="loginDisabled"
        :loading="loading"
        :keyboard="true"
        @click="handleValidateClick"
      >
        {{ $t('auth.login') }}
      </CPButton>

      <div class="flex w-full items-center justify-center">
        <CPDivider class="flex-1">{{ $t('auth.or') }}</CPDivider>
      </div>

      <CPButton
        :id="createElementId('login-modal', 'secure-code', 'button')"
        class="w-full rounded-lg"
        type="secondary"
        size="md"
        @click="
          () =>
            emit('changeScreen', {
              page: Screens.LOGIN_WITH_SECURE_CODE,
              data: { email: formValue.email },
            })
        "
      >
        {{ $t('auth.loginWithSecureCode') }}
      </CPButton>

      <div class="text-secondary flex w-full justify-center items-center gap-1">
        <CPText size="lg" weight="semibold" html-tag="span">
          {{ $t('auth.newUser') }}
        </CPText>
        <CPButton
          :id="createElementId('login-modal', 'signup', 'button')"
          type="text"
          color="secondary"
          size="xs"
          class="cursor-pointer hover:underline"
          @click="
            emit('changeScreen', {
              page: Screens.SIGN_UP,
              data: { email: formValue.email },
            })
          "
        >
          {{ $t('auth.signUp') }}
        </CPButton>
      </div>
    </CPForm>
  </div>
</template>

<style scoped>
  .form-item {
    height: 66px !important;
  }

  .divider-text {
    font-size: 14px !important;
    font-weight: 400;
  }

  .n-divider:not(.n-divider--vertical) {
    margin-top: 12px;
    margin-bottom: 0px;
  }
</style>
